// import logo from './logo.svg';
import './App.css';
import { Box, ChakraProvider, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import customTheme from './utils/customTheme';
import Sidebar from './components/sidebar/Sidebar';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';
import routes from './routes';
import { useEffect, useState } from 'react';
import IsDesktop from './utils/IsDesktop';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { loadGoogleAnalytics } from './helpers/analytics';
import classNames from 'classnames';
import { useTimeSlotPrompt } from './hooks/useTimeSlotPrompt';
import { useTermsheetNotification } from './hooks/useTermsheetNotification';
import TimeSlotPrompt from './components/TimeSlotPrompt';
import ServerErrorPage from './pages/error/ServerErrorPage';
import { PromptQueueProvider, usePromptQueue } from './context/PromptContext';
import moment from 'moment';
import UserService from './utils/UserService';
import TermsheetNotification from './components/TermsheetNotification';
import client from './libs/client';
import { setSubscriptionPlans } from './redux-toolkit/reducers/userSlice';

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_SIGN_IN}>
      <ChakraProvider theme={customTheme}>
        <Router>
          <PromptQueueProvider>
            <AppContent />
          </PromptQueueProvider>
        </Router>
      </ChakraProvider>
    </GoogleOAuthProvider>
  );
}

function AppContent() {
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isHidden, setIsHidden] = useState(false);
  const isDesktop = IsDesktop();
  const location = useLocation();
  const ref = encodeURIComponent(location.pathname + location.search);
  const lastDismissed = UserService.getTimeSlotPromptDismissed();
  const accountType = useSelector(
    (state) => state?.user?.userInfo?.userTypeTitle,
  );
  const subscriptionPlans = useSelector((state) => state?.user?.subscriptionPlans);

  const loggedInUser = useSelector((state) => state?.user?.userInfo);
  const dispatch = useDispatch();


  const { shouldShowPrompt, setShouldShowPrompt } = useTimeSlotPrompt();
  const { shouldShowNotification, setShouldShowNotification } =
    useTermsheetNotification();

  const { enqueuePrompt } = usePromptQueue();

  const toggleSidebar = () => {
    setSidebarWidth(sidebarWidth === 300 ? 130 : 300);
    setIsHidden(!isHidden);
  };

  useEffect(() => {
    const TRACKING_ID = process.env.REACT_APP_GA || '';
    if (TRACKING_ID) loadGoogleAnalytics(TRACKING_ID);
  }, []);

  useEffect(() => {
    let timer;
    const showTimeInMilliSeconds = 10_000;

    if (shouldShowPrompt && accountType === 'founder') {
      timer = setTimeout(() => {
        enqueuePrompt(TimeSlotPrompt, {
          setShouldShowPrompt,
        });
      }, showTimeInMilliSeconds);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [location.pathname, shouldShowPrompt, accountType, lastDismissed]);

  useEffect(() => {
    let timer;
    const showTimeInMilliSeconds = 10_000;

    if (shouldShowNotification && accountType === 'founder') {
      timer = setTimeout(() => {
        enqueuePrompt(TermsheetNotification, {
          setShouldShowNotification,
        });
      }, showTimeInMilliSeconds);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [location.pathname, shouldShowNotification, enqueuePrompt]);

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const isPublic = location.pathname.includes('public');

  // const accountType = null;


  const getSubscriptionPlans = async () => {
    if (loggedInUser?.userTypeId) {
      try {
        const response = await client().get('subscription/plans?user_type_id=' + loggedInUser?.userTypeId);
        return response.data;
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      if (!subscriptionPlans) {
        try {
          const plans = await getSubscriptionPlans();
          dispatch(setSubscriptionPlans(plans));
        } catch (error) {
          console.error('Failed to fetch subscription plans:', error);
        }
      }
    };


    fetchPlans();
  }, [subscriptionPlans, dispatch]);

  const authRoutes = routes.publicRoutes.map(({ path, element: Component }) => (
    <Route key={path} path={path} element={<Component />} />
  ));

  const serverErrorRoute = (
    <Route key="error" path="/error" element={<ServerErrorPage />} />
  );

  const privateRoutes = routes.privateRoutes.map(
    ({ path, element: Component }) => (
      <Route
        key={path}
        path={path}
        element={
          <Flex
            overflowY={'hidden'}
            className={`${isDesktop ? 'h-[100vh]' : ''} min-h-[100vh] ${path !== 'survey' ? '' : 'flex-col'}`}
          >
            {path !== 'survey' && (
              <Box
                className={classNames({
                  'h-[100vh]': isDesktop,
                })}
              >
                <Sidebar
                  sidebarWidth={sidebarWidth}
                  toggleSidebar={toggleSidebar}
                  isHidden={isHidden}
                />
              </Box>
            )}

            <Component />
          </Flex>
        }
      />
    ),
  );

  const founderRoute = routes.foundersRoutes.map(
    ({ path, element: Component }, index) => (
      <Route
        key={index}
        path={path}
        element={
          <Flex
            overflowY={'hidden'}
            className={`${isDesktop || path === 'connections' ? 'h-[100vh]' : ''} min-h-[100vh] w-[100vw]`}
          >
            <Sidebar
              sidebarWidth={sidebarWidth}
              toggleSidebar={toggleSidebar}
              isHidden={isHidden}
            />
            <Component />
          </Flex>
        }
      />
    ),
  );

  const investorRoute = routes.investorsRoutes.map(
    ({ path, element: Component }, index) => (
      <Route
        key={index}
        path={path}
        element={
          <Flex
            overflowY={'hidden'}
            className={`${isDesktop || path === 'connections' ? 'h-[100vh]' : ''} min-h-[100vh]`}
          >
            <Sidebar
              sidebarWidth={sidebarWidth}
              toggleSidebar={toggleSidebar}
              isHidden={isHidden}
            />
            <Component />
          </Flex>
        }
      />
    ),
  );

  const expertRoute = routes.expertsRoutes.map(
    ({ path, element: Component }, index) => (
      <Route
        key={index}
        path={path}
        element={
          <Flex
            overflowY={path === 'connections' ? 'auto' : 'hidden'}
            className={`${isDesktop ? 'h-[100vh]' : ''} min-h-[100vh]`}
          >
            <Sidebar
              sidebarWidth={sidebarWidth}
              toggleSidebar={toggleSidebar}
              isHidden={isHidden}
            />
            <Component />
          </Flex>
        }
      />
    ),
  );

  const adminRoute = routes.adminRoutes.map(
    ({ path, element: Component }, index) => (
      <Route
        key={index}
        path={path}
        element={
          <Flex
            overflowY={path === 'connections' ? 'auto' : 'hidden'}
            className={`${isDesktop ? 'h-[100vh]' : ''} min-h-[100vh]`}
          >
            <Sidebar
              sidebarWidth={sidebarWidth}
              toggleSidebar={toggleSidebar}
              isHidden={isHidden}
            />
            <Component />
          </Flex>
        }
      />
    ),
  );

  const redirectLogin = (
    <Route
      key="redirect"
      path="*"
      element={<Navigate to={`/login?ref=${ref}`} replace />}
    />
  );
  const redirectSurvey = (
    <Route key="redirect" path="*" element={<Navigate to="/survey" />} />
  );

  const getRoutes = () => {
    if (isAuthenticated && accountType === 'expert') {
      return expertRoute;
    } else if (isAuthenticated && accountType === 'founder') {
      return founderRoute;
    } else if (isAuthenticated && accountType === 'investor') {
      return investorRoute;
    } else if (
      isAuthenticated &&
      (accountType === 'admin' || accountType === 'sub_admin')
    ) {
      return adminRoute;
    } else if (
      isAuthenticated &&
      (accountType === null || accountType === '')
    ) {
      return redirectSurvey;
    } else {
      return redirectLogin;
    }
  };

  return (
    <>
      <Routes>
        {authRoutes}
        {serverErrorRoute}
        {isAuthenticated ? privateRoutes : isPublic && redirectLogin}
        {getRoutes()}
      </Routes>
    </>
  );
}

export default App;
